import { Controller } from "@hotwired/stimulus"

// Drives the attachment viewer modal's tab strip. The server renders one tab per
// sibling attachment in the current context (the task strip or process rail the
// modal was opened from), already in the right order, so left/right arrow keys
// just step to the adjacent tab — browser-style.
export default class extends Controller {
  static targets = ["tab"]

  connect() {
    this.focusActiveTab()
  }

  next(event) {
    if (this.isTypingTarget(event)) return
    // Own the arrow key fully so it never leaks to the page behind the modal
    // (which has its own keyboard shortcuts) or triggers a browser default —
    // even when we're already on the last tab and there's nowhere to move.
    event.preventDefault()
    event.stopPropagation()
    this.step(1)
  }

  prev(event) {
    if (this.isTypingTarget(event)) return
    event.preventDefault()
    event.stopPropagation()
    this.step(-1)
  }

  step(delta) {
    const tabs = this.tabTargets
    if (tabs.length < 2) return

    const currentIndex = tabs.findIndex((tab) => tab.dataset.active === "true")
    if (currentIndex === -1) return

    const nextTab = tabs[currentIndex + delta]
    if (nextTab) nextTab.click()
  }

  // After each navigation the modal reloads, so move focus onto the now-active
  // tab. Otherwise focus restoration lands on the first sibling link, leaving a
  // stray focus ring on a tab that isn't the selected one.
  focusActiveTab() {
    const activeTab = this.tabTargets.find((tab) => tab.dataset.active === "true")
    if (!activeTab) return
    activeTab.focus({ preventScroll: true })
    activeTab.scrollIntoView({ block: "nearest", inline: "nearest" })
  }

  // Don't hijack arrow keys while the user is typing in a field (e.g. the CSV
  // viewer's column filters), where left/right should move the text caret.
  isTypingTarget(event) {
    const el = event?.target
    if (!el) return false

    const tag = el.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true
  }
}
